// src/reasoningTests/ReasoningTestConfiguration.js
import { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  Autocomplete,
  TextField,
  FormGroup,
  FormControlLabel,
  Checkbox,
  Alert,
  CircularProgress,
  Chip,
  Divider,
  ToggleButtonGroup,
  ToggleButton,
  Switch,
} from '@mui/material';
import { createFilterOptions } from '@mui/material/Autocomplete';
import {
  Send as SendIcon,
  Person as PersonIcon,
  Psychology as PsychologyIcon,
  Groups as GroupsIcon,
  Email as EmailIcon,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import TestReminderFields, { DEFAULT_REMINDER_CONFIG } from '../layouts/TestReminderFields';
import useStore from '../config/store';
import api from '../config/api';
import ReasoningTestBulkSend from './ReasoningTestBulkSend';

const applicantFilter = createFilterOptions({
  stringify: (option) => `${option?.name || ''}`,
  trim: true,
});

const ReasoningTestConfiguration = () => {
  const { t } = useTranslation();
  const { authToken, project, organization, user, showAlert, language } = useStore();

  // Estados del formulario
  const [selectedApplicant, setSelectedApplicant] = useState(null);
  const [selectedTests, setSelectedTests] = useState({
    wordAssociation: false,
    logicalProcess: false,
    numericalReasoning: false,
    socialContext: false,
    logicalAbstract: false,
  });
  const [sendEmail, setSendEmail] = useState(true);
  const [reminderConfig, setReminderConfig] = useState(DEFAULT_REMINDER_CONFIG);

  // Estados de datos
  const [applicants, setApplicants] = useState([]);

  // Estados de carga
  const [loadingApplicants, setLoadingApplicants] = useState(false);
  const [sending, setSending] = useState(false);

  // Modo de envío: 'individual' o 'bulk'
  const [sendMode, setSendMode] = useState('individual');

  // Cargar postulantes
  useEffect(() => {
    const loadApplicants = async () => {
      if (!authToken || !project) return;

      try {
        setLoadingApplicants(true);
        const response = await api.get(
          `/applicants`,
          {
            params: { projectId: project },
            headers: { Authorization: `Bearer ${authToken}` },
          }
        );

        if (response.data.response?.status_response === 'OK') {
          setApplicants((response.data.response.data || []).sort((a, b) => (a.name || '').localeCompare(b.name || '', 'es', { sensitivity: 'base' })));
        }
      } catch (error) {
        console.error('Error loading applicants:', error);
        showAlert(t('reasoningTests.loadApplicantsError'), 'error');
      } finally {
        setLoadingApplicants(false);
      }
    };

    loadApplicants();
  }, [authToken, project, showAlert, t]);

  // Manejar cambio en checkboxes de tests
  const handleTestChange = (event) => {
    setSelectedTests({
      ...selectedTests,
      [event.target.name]: event.target.checked,
    });
  };

  // Validar formulario
  const isFormValid = () => {
    if (!selectedApplicant) return false;
    const hasSelectedTests = Object.values(selectedTests).some(val => val);
    return hasSelectedTests;
  };

  // Obtener lista de tests seleccionados
  const getSelectedTestTypes = () => {
    return Object.keys(selectedTests).filter(key => selectedTests[key]);
  };

  // Enviar tests
  const handleSendTests = async () => {
    if (!isFormValid()) {
      showAlert(t('reasoningTests.pleaseSelectApplicantAndTests'), 'warning');
      return;
    }

    try {
      setSending(true);
      const testTypes = getSelectedTestTypes();

      const testData = {
        applicantId: selectedApplicant.uid,
        applicantName: selectedApplicant.name,
        applicantEmail: selectedApplicant.email,
        testTypes: testTypes,
        projectId: project,
        organizationId: organization,
        language: language || 'es',
        sendEmail: sendEmail,
        reminderConfig,
      };

      const response = await api.post(
        '/reasoning-tests/batch',
        testData,
        {
          headers: { Authorization: `Bearer ${authToken}` },
        }
      );

      if (response.data.errorCode === 0) {
        const count = response.data.response.sentCount || response.data.response.testsCreated;
        showAlert(
          sendEmail
            ? t('reasoningTests.testsCreatedAndEmailSent', { count })
            : t('reasoningTests.testsCreatedSuccess', { count }),
          'success'
        );
        resetForm();
      } else {
        showAlert(response.data.errorMessage || t('reasoningTests.testSentError'), 'error');
      }

    } catch (error) {
      console.error('Error sending reasoning tests:', error);
      const errorMessage = error.response?.data?.response?.message || t('reasoningTests.testSentError');
      showAlert(errorMessage, 'error');
    } finally {
      setSending(false);
    }
  };

  // Resetear formulario
  const resetForm = () => {
    setSelectedApplicant(null);
    setSelectedTests({
      wordAssociation: false,
      logicalProcess: false,
      numericalReasoning: false,
      socialContext: false,
      logicalAbstract: false,
    });
    setSendEmail(true);
    setReminderConfig(DEFAULT_REMINDER_CONFIG);
  };

  return (
    <Box>
      {/* Toggle entre Individual y Masivo */}
      {user.rol === "super-admin" && <Box sx={{ mb: 3 }}>
        <ToggleButtonGroup
          value={sendMode}
          exclusive
          onChange={(e, newMode) => newMode && setSendMode(newMode)}
        >
          <ToggleButton value="individual">
            <PersonIcon sx={{ mr: 1 }} />
            {t('disc.bulkSend.individualSend')}
          </ToggleButton>
          <ToggleButton value="bulk">
            <GroupsIcon sx={{ mr: 1 }} />
            {t('disc.bulkSend.bulkSend')}
          </ToggleButton>
        </ToggleButtonGroup>
      </Box>}

      {/* MODO INDIVIDUAL */}
      {sendMode === 'individual' && (
        <Paper sx={{ p: 3, mb: 3 }}>
          <Typography variant="h5" gutterBottom>
            {t('reasoningTests.configurationTitle')}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            {t('reasoningTests.configurationSubTitle')}
          </Typography>

          {/* Selector de postulante */}
          <Box sx={{ mb: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <PersonIcon sx={{ mr: 1, color: 'primary.main' }} />
              <Typography variant="subtitle1">
                {t('reasoningTests.selectApplicant')}
              </Typography>
            </Box>

            <Autocomplete
              options={applicants}
              getOptionLabel={(option) => option?.name || ''}
              getOptionKey={(option) => option.id ?? option.uid ?? option.email}
              isOptionEqualToValue={(option, value) =>
                (option.id ?? option.uid ?? option.email) ===
                (value.id ?? value.uid ?? value.email)
              }
              filterOptions={applicantFilter}
              value={selectedApplicant}
              onChange={(event, newValue) => setSelectedApplicant(newValue)}
              loading={loadingApplicants}
              noOptionsText={t('noData')}
              renderInput={(params) => (
                <TextField
                  {...params}
                  placeholder={t('reasoningTests.searchApplicant')}
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {loadingApplicants ? <CircularProgress size={20} /> : null}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  }}
                />
              )}
              renderOption={(props, option) => (
                <li {...props} key={option.id ?? option.uid ?? option.email}>
                  <Box>
                    <Typography variant="body1">{option.name}</Typography>
                    <Typography variant="caption" color="text.secondary">
                      {option.email}
                    </Typography>
                  </Box>
                </li>
              )}
            />
          </Box>

          <Divider sx={{ mb: 3 }} />

          {/* Selector de tests */}
          <Box sx={{ mb: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <PsychologyIcon sx={{ mr: 1, color: 'primary.main' }} />
              <Typography variant="subtitle1">
                {t('reasoningTests.selectTests')}
              </Typography>
            </Box>

            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              {t('reasoningTests.selectTestsDescription')}
            </Typography>

            <FormGroup>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={selectedTests.wordAssociation}
                    onChange={handleTestChange}
                    name="wordAssociation"
                    color="primary"
                  />
                }
                label={
                  <Box>
                    <Typography variant="body1">
                      {t('reasoningTests.wordAssociation')}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {t('reasoningTests.wordAssociationDescription')}
                    </Typography>
                  </Box>
                }
              />

              <FormControlLabel
                control={
                  <Checkbox
                    checked={selectedTests.logicalProcess}
                    onChange={handleTestChange}
                    name="logicalProcess"
                    color="primary"
                  />
                }
                label={
                  <Box>
                    <Typography variant="body1">
                      {t('reasoningTests.logicalProcess')}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {t('reasoningTests.logicalProcessDescription')}
                    </Typography>
                  </Box>
                }
              />

              <FormControlLabel
                control={
                  <Checkbox
                    checked={selectedTests.numericalReasoning}
                    onChange={handleTestChange}
                    name="numericalReasoning"
                    color="primary"
                  />
                }
                label={
                  <Box>
                    <Typography variant="body1">
                      {t('reasoningTests.numericalReasoning')}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {t('reasoningTests.numericalReasoningDescription')}
                    </Typography>
                  </Box>
                }
              />

              <FormControlLabel
                control={
                  <Checkbox
                    checked={selectedTests.socialContext}
                    onChange={handleTestChange}
                    name="socialContext"
                    color="primary"
                  />
                }
                label={
                  <Box>
                    <Typography variant="body1">
                      {t('reasoningTests.socialContext')}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {t('reasoningTests.socialContextDescription')}
                    </Typography>
                  </Box>
                }
              />

              <FormControlLabel
                control={
                  <Checkbox
                    checked={selectedTests.logicalAbstract}
                    onChange={handleTestChange}
                    name="logicalAbstract"
                    color="primary"
                  />
                }
                label={
                  <Box>
                    <Typography variant="body1">
                      {t('reasoningTests.logicalAbstract')}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {t('reasoningTests.logicalAbstractDescription')}
                    </Typography>
                  </Box>
                }
              />
            </FormGroup>

            {/* Mostrar tests seleccionados */}
            {getSelectedTestTypes().length > 0 && (
              <Box sx={{ mt: 2 }}>
                <Typography variant="caption" color="text.secondary" gutterBottom>
                  {t('reasoningTests.selectedTests')}:
                </Typography>
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mt: 1 }}>
                  {getSelectedTestTypes().map((testType) => (
                    <Chip
                      key={testType}
                      label={t(`reasoningTests.${testType}`)}
                      color="secondary"
                      size="small"
                    />
                  ))}
                </Box>
              </Box>
            )}
          </Box>

          {/* Alerta de validación */}
          {!isFormValid() && selectedApplicant && getSelectedTestTypes().length === 0 && (
            <Alert severity="info" sx={{ mb: 2 }}>
              {t('reasoningTests.pleaseSelectAtLeastOneTest')}
            </Alert>
          )}

          {/* Switch enviar email */}
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 3, gap: 1 }}>
            <EmailIcon sx={{ color: sendEmail ? 'primary.main' : 'action.disabled' }} />
            <FormControlLabel
              control={
                <Switch
                  checked={sendEmail}
                  onChange={(e) => setSendEmail(e.target.checked)}
                  color="primary"
                />
              }
              label={
                <Box>
                  <Typography variant="body2">
                    {t('reasoningTests.sendEmailOnCreate')}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {sendEmail
                      ? t('reasoningTests.sendEmailOnCreateEnabled')
                      : t('reasoningTests.sendEmailOnCreateDisabled')
                    }
                  </Typography>
                </Box>
              }
            />
          </Box>

          {/* Recordatorio automatico: solo aplica cuando el correo lo manda el sistema */}
          <TestReminderFields
            show={sendEmail}
            value={reminderConfig}
            onChange={setReminderConfig}
          />

          {/* Botones */}
          <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
            <Button
              variant="outlined"
              onClick={resetForm}
              disabled={sending}
            >
              {t('reasoningTests.cancel')}
            </Button>
            <Button
              variant="contained"
              startIcon={sending ? <CircularProgress size={20} color="inherit" /> : <SendIcon />}
              onClick={handleSendTests}
              disabled={!isFormValid() || sending}
            >
              {sending
                ? t('personalHabilitiesTests.sending')
                : sendEmail
                  ? t('personalHabilitiesTests.sendTests')
                  : t('reasoningTests.createTests')
              }
            </Button>
          </Box>
        </Paper>
      )}

      {/* MODO MASIVO */}
      {sendMode === 'bulk' && (
        <ReasoningTestBulkSend
          applicants={applicants}
        />
      )}
    </Box>
  );
};

export default ReasoningTestConfiguration;